import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { Popover } from '@headlessui/react';
import './parameter-dialog.css';
import { getTooltip } from '../tooltips';

/**
 * Generic parameter editing dialog. Provide an array of field configs and it will
 * render appropriate form controls. Supports text, number, select and checkbox.
 *
 * Field config example:
 * {
 *   key: 'base_persona',
 *   label: 'Base Persona',
 *   type: 'select',
 *   options: [ { value:'professional_interviewer', label:'Professional Interviewer' }, ... ],
 *   tooltip: 'Defines Sam\'s overarching personality profile',
 *   min:1, max:10 // for number
 * }
 */
const ParameterDialog = ({
  open,
  title = 'Edit Parameters',
  description = '',
  fields = [],
  values = {},
  onClose,
  onSave
}) => {
  const DEFAULTS = {
    base_persona: 'professional_interviewer',
    consistency_rules: 'medium',
    emotional_responsiveness: 'medium',
    max_clarification_attempts: 3,
    correction_policy: 'always_allow',
    completion_requirements: 'all_questions'
  };
  const initialMerged = { ...DEFAULTS, ...values };
  const [localValues, setLocalValues] = useState(initialMerged);

  React.useEffect(() => {
    if (open) {
      setLocalValues({ ...DEFAULTS, ...values });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleChange = (key, value) => {
    setLocalValues(prev => ({ ...prev, [key]: value }));
  };

  const renderField = (field) => {
    const { key, label, type, options = [], tooltip, min, max, step } = field;
    const value = localValues[key] ?? '';

    const tipText = tooltip || getTooltip(key);

    return (
      <div className="pd-field" key={key} style={{ marginBottom: '12px' }}>
        <label className="pd-label">
          {label}
          {tipText && (
            <Popover className="pd-pop">
              <Popover.Button className="pd-tooltip" aria-label="Help">ⓘ</Popover.Button>
              <Popover.Panel className="pd-tooltip-panel">{tipText}</Popover.Panel>
            </Popover>
          )}
        </label>
        {type === 'select' && (
          <select
            value={value}
            onChange={(e) => handleChange(key, e.target.value)}
            onMouseDown={(e)=> e.stopPropagation()}
            onClick={(e)=> e.stopPropagation()}
            className="pd-control"
          >
            <option value="">-- Select --</option>
            {options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        )}
        {type === 'number' && (
          <input
            type="number"
            value={value}
            min={min}
            max={max}
            step={step || 1}
            onChange={(e) => handleChange(key, e.target.value ? Number(e.target.value) : '')}
            onMouseDown={(e)=> e.stopPropagation()}
            onClick={(e)=> e.stopPropagation()}
            className="pd-control"
          />
        )}
        {type === 'checkbox' && (
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => handleChange(key, e.target.checked)}
            onMouseDown={(e)=> e.stopPropagation()}
            onClick={(e)=> e.stopPropagation()}
          />
        )}
        {type === 'text' && (
          <input
            type="text"
            value={value}
            onChange={(e) => handleChange(key, e.target.value)}
            onMouseDown={(e)=> e.stopPropagation()}
            onClick={(e)=> e.stopPropagation()}
            className="pd-control"
          />
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} className="pd-dialog">
      <div className="pd-backdrop" aria-hidden="true" />
      <Dialog.Panel className="pd-panel">
        <Dialog.Title className="pd-title">{title}</Dialog.Title>
        {description && <Dialog.Description className="pd-desc">{description}</Dialog.Description>}
        <div className="pd-body">
          {fields.map(renderField)}
        </div>
        <div className="pd-footer">
          <button className="pd-btn" onClick={() => onClose()}>Cancel</button>
          <button className="pd-btn pd-primary" onClick={() => { onSave(localValues); onClose(); }}>Save</button>
        </div>
      </Dialog.Panel>
    </Dialog>
  );
};

export default ParameterDialog; 